// src/hooks/useKeepOnBoard.ts
import { RefObject, useEffect } from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';

// Miejsce w wolnej przestrzeni planszy jako ułamek 0–1 (0 — przy lewej albo górnej krawędzi). Ograniczenie do 0–1
// ściąga z powrotem także to, co już wystawało za krawędź
const toRatio = (position: number, freeSpace: number) => (
  freeSpace > 0 ? gsap.utils.clamp(0, 1, position / freeSpace) : 0
);

// Karty i obrazki w Sparuj leżą na planszy w stałych miejscach (w pikselach), więc po zmniejszeniu ekranu część z nich
// wypadała za krawędź. Po każdej zmianie rozmiaru planszy (szerokość okna, obrót telefonu) element przesuwa się
// proporcjonalnie: zostaje ten sam rozrzut, tylko ściśnięty albo rozciągnięty. Planszą jest element, względem którego
// karta ma położenie (offsetParent — #main z AppContent).
// getTop podaje, od jakiej wysokości zaczyna się wolne miejsce — w Sparuj Easy pod licznikiem plansz, żeby nic na niego
// nie weszło. Bez getTop wolne miejsce zaczyna się od górnej krawędzi planszy
const useKeepOnBoard = (elementRef: RefObject<HTMLElement>, getTop?: () => number) => {
  useEffect(() => {
    const element = elementRef.current;
    const board = element?.offsetParent;

    if (!element || !(board instanceof HTMLElement)) return undefined;

    let lastWidth = board.clientWidth;
    let lastHeight = board.clientHeight;
    let lastTop = getTop?.() ?? 0;

    const observer = new ResizeObserver(() => {
      const width = board.clientWidth;
      const height = board.clientHeight;

      if (width === lastWidth && height === lastHeight) return;

      const top = getTop?.() ?? 0;

      // Elementu zdjętego już z planszy ani tego, który dziecko akurat trzyma, nie ruszamy
      if (element.isConnected && !Draggable.get(element)?.isDragging) {
        // Położenie: miejsce z rozrzucenia (left/top) plus przesunięcie z przeciągania (x/y)
        const x = element.offsetLeft + (gsap.getProperty(element, 'x') as number || 0);
        const y = element.offsetTop + (gsap.getProperty(element, 'y') as number || 0);
        const newX = toRatio(x, lastWidth - element.offsetWidth) * Math.max(width - element.offsetWidth, 0);
        // W pionie wolne miejsce zaczyna się od top, nie od 0
        const ratioY = toRatio(y - lastTop, lastHeight - element.offsetHeight - lastTop);
        const newY = top + ratioY * Math.max(height - element.offsetHeight - top, 0);

        gsap.set(element, { x: `+=${newX - x}`, y: `+=${newY - y}` });
      }

      lastWidth = width;
      lastHeight = height;
      lastTop = top;
    });

    observer.observe(board);

    return () => observer.disconnect();
  }, [elementRef, getTop]);
};

export default useKeepOnBoard;
